/* global React */

// Project image URL → optional WebP source URL.
// Returns null if no smaller WebP twin exists (per content/_webp.js).
function dzWebp(jpgUrl) {
  if (!jpgUrl) return null;
  var stem = jpgUrl.replace(/^.*\//, '').replace(/\.[^.]+$/, '');
  var list = (typeof window !== 'undefined' && window.DZ_WEBP_AVAILABLE) || [];
  return list.indexOf(stem) !== -1 ? jpgUrl.replace(/\.jpg$/i, '.webp') : null;
}

// Project image URL → optional AVIF source URL. Same gate as WebP
// but for AVIF. Browsers pick AVIF first (smallest), then WebP,
// then JPG fallback. AVIF support: ~94% global, all modern browsers.
function dzAvif(jpgUrl) {
  if (!jpgUrl) return null;
  var stem = jpgUrl.replace(/^.*\//, '').replace(/\.[^.]+$/, '');
  var list = (typeof window !== 'undefined' && window.DZ_AVIF_AVAILABLE) || [];
  return list.indexOf(stem) !== -1 ? jpgUrl.replace(/\.jpg$/i, '.avif') : null;
}
function dzAvifMobile(jpgUrl) {
  var desktop = dzAvif(jpgUrl);
  return desktop ? desktop.replace(/\.avif$/i, '-mobile.avif') : null;
}

// Project image URL → {w, h} so <img> can declare width/height
// attributes. Prevents CLS — the browser reserves the right slot
// before the image arrives. Falls back to a 16:10 reference frame
// if the stem isn't in the dims map.
function dzDims(jpgUrl) {
  if (!jpgUrl) return { w: 1600, h: 1000 };
  var stem = jpgUrl.replace(/^.*\//, '').replace(/\.[^.]+$/, '');
  var dims = (typeof window !== 'undefined' && window.DZ_IMAGE_DIMS) || {};
  return dims[stem] || { w: 1600, h: 1000 };
}

// Build an Image-SERP-friendly alt string from project data.
// Combines name + category + location + optional descriptive tags,
// so a bare `<img alt={project.name}>` (just "Six Senses Amaala")
// becomes "Six Senses Hotel — AMAALA Red Sea — BIM Project — Saudi
// Arabia — hospitality, ISO 19650, Vision 2030". The richer alt
// wins on image search for keywords the bare name alone misses.
function dzAlt(item) {
  if (!item) return '';
  var locParts = [];
  if (item.location) locParts.push(item.location);
  if (item.country) locParts.push(item.country);
  var parts = [
    item.name,
    item.categoryLabel,
    locParts.join(', ')
  ];
  if (Array.isArray(item.imgAltTags) && item.imgAltTags.length) {
    parts.push(item.imgAltTags.join(', '));
  }
  return parts.filter(Boolean).join(' — ');
}
/* ──────────────────────────────────────────────────────────────────────
   ProjectsPage — /projects/
   Interactive portfolio:
     ▸ Stats strip with CountUp on scroll
     ▸ Featured card with cursor-tracking aqua spotlight
     ▸ Filter chips (All / BIM / Concept / Interior / VR) with smooth transitions
     ▸ Scroll-triggered card reveals via IntersectionObserver, 60ms stagger
   Cards open detail anchors for now (#slug); detail pages come later.
   ────────────────────────────────────────────────────────────────────── */

const { useState: useProjState, useEffect: useProjEffect, useRef: useProjRef } = React;

/* ─── CountUp — scoped variant for the stats strip ─── */
function ProjCountUp({ end, duration = 1800, suffix = '' }) {
  const [val, setVal] = useProjState(0);
  const ref = useProjRef(null);
  const started = useProjRef(false);

  useProjEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - t0) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setVal(Math.round(end * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

/* ─── Card with scroll-triggered reveal (per-card stagger via inline delay) ─── */
function ProjectCard({ item, index }) {
  const [visible, setVisible] = useProjState(false);
  const ref = useProjRef(null);

  useProjEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={'dz-projects__card-reveal' + (visible ? ' is-visible' : '')}
      style={{ transitionDelay: (index * 60) + 'ms' }}
    >
      <article className="dz-projects__card">
        <a href={item.hasDetailPage ? (window.location.pathname.startsWith('/ar/') ? '/ar' : '') + '/projects/' + item.slug + '/' : '#' + item.slug} className="dz-projects__card-link">
          <div className="dz-projects__card-image">
            <picture>
              {dzAvifMobile(item.image) && <source media="(max-width: 800px)" srcSet={dzAvifMobile(item.image)} type="image/avif" />}
              {dzAvif(item.image) && <source srcSet={dzAvif(item.image)} type="image/avif" />}
              {dzWebp(item.image) && <source srcSet={dzWebp(item.image)} type="image/webp" />}
              <img src={item.image} alt={dzAlt(item)} loading="lazy" decoding="async"
                   width={dzDims(item.image).w} height={dzDims(item.image).h} />
            </picture>
          </div>
          <div className="dz-projects__card-body">
            <h3 className="dz-projects__card-name">{item.name}</h3>
            <p className="dz-projects__card-meta">
              <span>{item.location}{item.country ? ', ' + item.country : ''}</span>
            </p>
            <p className="dz-projects__card-scope">{item.scope}</p>
            <div className="dz-projects__card-tags">
              {(item.tags || []).slice(0, 2).map((t) => (
                <span key={t} className="dz-projects__chip">{t}</span>
              ))}
            </div>
          </div>
        </a>
      </article>
    </div>
  );
}

/* ─── Featured card with cursor-tracking aqua spotlight ─── */
function FeaturedCard({ project }) {
  const ref = useProjRef(null);

  const handleMouseMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    el.style.setProperty('--mx', x + '%');
    el.style.setProperty('--my', y + '%');
  };

  const handleMouseLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--mx', '50%');
    el.style.setProperty('--my', '50%');
  };

  return (
    <article className="dz-projects__featured" aria-label="Featured project">
      <a
        ref={ref}
        href={project.hasDetailPage ? (window.location.pathname.startsWith('/ar/') ? '/ar' : '') + '/projects/' + project.slug + '/' : '#' + project.slug}
        className="dz-projects__featured-link"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div className="dz-projects__featured-image">
          <picture>
            {dzAvifMobile(project.image) && <source media="(max-width: 800px)" srcSet={dzAvifMobile(project.image)} type="image/avif" />}
            {dzAvif(project.image) && <source srcSet={dzAvif(project.image)} type="image/avif" />}
            {dzWebp(project.image) && <source srcSet={dzWebp(project.image)} type="image/webp" />}
            <img
              src={project.image}
              alt={dzAlt(project)}
              loading="eager"
              decoding="async"
              width={dzDims(project.image).w}
              height={dzDims(project.image).h}
            />
          </picture>
        </div>
        <div className="dz-projects__featured-body">
          <span className="dz-projects__featured-tag">Featured project</span>
          <h2 className="dz-projects__featured-name">{project.name}</h2>
          <p className="dz-projects__featured-meta">
            <span>{project.location}{project.country ? ', ' + project.country : ''}</span>
            {project.client ? <><span className="dz-projects__sep">·</span><span>{project.client}</span></> : null}
          </p>
          <p className="dz-projects__featured-scope">{project.scope}</p>
          <div className="dz-projects__featured-tags">
            {(project.tags || []).map((t) => (
              <span key={t} className="dz-projects__chip">{t}</span>
            ))}
          </div>

          {/* "Read more" CTA — only appears when the project has a real detail page,
              so we never show a "Read more" link that goes to a 404 anchor. */}
          {project.hasDetailPage ? (
            <span className="dz-projects__featured-cta" aria-hidden="true">
              <span>Read more</span>
              <span className="dz-projects__featured-cta-arrow">→</span>
            </span>
          ) : null}
        </div>
      </a>
    </article>
  );
}

/* ─── Page ─── */
function ProjectsPage() {
  const data = (typeof window !== 'undefined' && window.DZ_PROJECTS) || {};
  const items = (data.items || []);
  const [activeFilter, setActiveFilter] = useProjState('all');

  if (!data.hero || items.length === 0) return null;

  const featured = items.find((i) => i.featured);
  const others = items.filter((i) => !i.featured);
  const byCategory = (catId) => others.filter((i) => i.category === catId);

  const filterChips = [
    { id: 'all', label: 'All work' },
    ...((data.categories || []).map((c) => ({ id: c.id, label: c.label })))
  ];

  return (
    <main className="dz-projects" id="dz-main" aria-labelledby="projects-title">
      <div className="dz-projects__inner">

        {/* ── Intro ── */}
        <header className="dz-projects__intro">
          <span className="dz-projects__eyebrow">{data.hero.eyebrow}</span>
          <h1 id="projects-title" className="dz-projects__title">
            {data.hero.titleLeading} <em>{data.hero.titleAccent}</em>
          </h1>
          <p
            className="dz-projects__lead"
            dangerouslySetInnerHTML={{ __html: data.hero.intro }}
          />
          {data.hero.cta && (
            <div className="dz-projects__hero-cta">
              <a className="dz-btn dz-btn--aqua" href={data.hero.cta.href}>{data.hero.cta.label}</a>
            </div>
          )}
        </header>

        {/* ── Stats strip ── */}
        {data.stats && data.stats.length > 0 ? (
          <section className="dz-projects__stats" aria-label="Portfolio stats">
            <ul className="dz-projects__stats-grid">
              {data.stats.map((s, i) => (
                <li key={i} className="dz-projects__stat">
                  <span className="dz-projects__stat-num">
                    <ProjCountUp end={s.num} duration={1600 + i * 200} suffix={s.suffix || ''} />
                  </span>
                  <span className="dz-projects__stat-label">{s.label}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* ── Featured project ── */}
        {featured ? <FeaturedCard project={featured} /> : null}

        {/* ── Filter chips ── */}
        <div className="dz-projects__filters" role="tablist" aria-label="Filter projects by category">
          {filterChips.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={activeFilter === f.id}
              className={'dz-projects__filter' + (activeFilter === f.id ? ' is-active' : '')}
              onClick={() => setActiveFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* ── Categorized grids (filtered) ── */}
        {(data.categories || []).map((cat) => {
          if (activeFilter !== 'all' && activeFilter !== cat.id) return null;
          const catItems = byCategory(cat.id);
          if (catItems.length === 0) return null;

          return (
            <section key={cat.id} className="dz-projects__category" aria-label={cat.label}>
              <header className="dz-projects__category-head">
                <div className="dz-projects__category-headtext">
                  <h2 className="dz-projects__category-title">{cat.label}</h2>
                  {cat.sub ? <p className="dz-projects__category-sub">{cat.sub}</p> : null}
                </div>
                <span className="dz-projects__category-count">
                  {catItems.length} project{catItems.length === 1 ? '' : 's'}
                </span>
              </header>

              <div className="dz-projects__grid">
                {catItems.map((item, idx) => (
                  <ProjectCard key={item.slug} item={item} index={idx} />
                ))}
              </div>
            </section>
          );
        })}

        {/* ── CTA ── */}
        {data.cta ? (
          <section className="dz-projects__cta" aria-label="Start a project">
            <h2 className="dz-projects__cta-heading">{data.cta.heading}</h2>
            <p
              className="dz-projects__cta-sub"
              dangerouslySetInnerHTML={{ __html: data.cta.sub }}
            />
            <a href={data.cta.buttonHref} className="dz-projects__cta-button">
              {data.cta.buttonLabel}
              <span aria-hidden="true">→</span>
            </a>
          </section>
        ) : null}

      </div>
    </main>
  );
}

window.ProjectsPage = ProjectsPage;
